/**
 * Drop signature slots that have no tab on the PDF.
 *
 * The rule is per SLOT: a signature slot must have a signature tab, an initials slot must have an
 * initials tab. A slot with no tab is not meant to be there — Studio's canvas shows nothing for
 * it, so it is invisible to whoever opens the form, while the app still offers a slot nobody can
 * complete. Neither the signature block's own `pdfLocation` nor its `fields[]` are consulted, so
 * a signer carrying date/time fields but no signature tab is still pruned.
 *
 * An initialled signer is NOT exempt. Its initials slot stands on its own initials tab and is
 * untouched here; its signature slot, having no tab, goes. This function only prunes signatures —
 * initialsJSON is never edited, and a role emptied of signatures keeps its initials.
 *
 * That separation is only safe because initials now own their metadata: generateInitialsJSON
 * builds roles from the union of `signatures` and the form's stored initialsJSON, taking
 * integrationRole / min / default / altSigners from the latter. Initials used to inherit those
 * from the matching signatures role, so pruning a role took its initials — and its author-set
 * altSigners — with it. That coupling is why an earlier version of this rule kept initialled
 * signers.
 *
 * Two knock-on effects, both deliberate:
 *   - a role left with no signatures is removed from `signatures` entirely, rather than published
 *     as an empty role,
 *   - `signerAttributeBindings` are filtered to the surviving role+signer pairs, so no binding is
 *     left pointing at a signer that no longer exists.
 *
 * Also resets each surviving role's `max` to its slot count, `additionalSignaturePage` to
 * slots + 1, and pulls `default` down when it exceeds the new max, reporting what changed in
 * `slotFixes`. A role claiming max 6 with two slots placed
 * offers signers nowhere to sign. initialsJSON gets the same treatment in the builder, computed
 * from its own rows after empty ones are dropped.
 *
 * `hasInitials` reports whether the form is initialled at all. A form with neither signatures nor
 * initials placed signs nobody: pruning would empty signaturesJSON on what is probably unfinished,
 * so callers can treat that separately (the script flags and skips it by default).
 *
 * Shared by the Save dialog's "Prune if Not Annotated" and scripts/prune-save-publish.js so a
 * form pruned by hand and one pruned in bulk come out identical.
 *
 * @param {Object} state - { signatures, signatureAnnotations, signerAttributeBindings }
 * @returns {{ signatures: Object, signerAttributeBindings: Array, removed: Array, changed: boolean }}
 *          `removed` lists { roleKey, signerKey } for reporting.
 */
export function pruneUnannotatedSigners(state = {}) {
  const signatures = state.signatures || {};
  const signatureAnnotations = state.signatureAnnotations || [];
  const signerAttributeBindings = state.signerAttributeBindings || [];

  const annotatedKeys = new Set(signatureAnnotations.map((a) => a?.signerKey).filter(Boolean));

  const removed = [];
  const prunedEntries = Object.entries(signatures)
    .map(([roleKey, roleValue]) => {
      const all = roleValue?.signatures || [];
      const keep = (s) => annotatedKeys.has(s?.signerKey);
      const kept = all.filter(keep);
      for (const s of all) {
        if (!keep(s)) removed.push({ roleKey, signerKey: s?.signerKey ?? "" });
      }
      return [roleKey, { ...roleValue, signatures: kept }];
    })
    .filter(([, roleValue]) => (roleValue.signatures || []).length > 0);

  // Slot counts must match reality: max is how many of that role can sign, and there are only as
  // many places to sign as there are slots. additionalSignaturePage follows as slots + 1.
  const slotFixes = [];
  const withCounts = prunedEntries.map(([roleKey, roleValue]) => {
    const slots = (roleValue.signatures || []).length;
    const nextPage = slots + 1;
    // `default` is how many signers a new deal starts with, so it cannot exceed max. Lowering max
    // to the slot count can strand it above — a role with one slot defaulting to two signers.
    // Only ever pulled DOWN; a default already within range is left as the author set it.
    const currentDefault = roleValue.default;
    const nextDefault =
      typeof currentDefault === "number" && currentDefault > slots ? slots : currentDefault;

    if (
      roleValue.max !== slots ||
      roleValue.additionalSignaturePage !== nextPage ||
      nextDefault !== currentDefault
    ) {
      slotFixes.push({
        roleKey,
        slots,
        from: {
          max: roleValue.max,
          additionalSignaturePage: roleValue.additionalSignaturePage,
          default: currentDefault,
        },
        to: { max: slots, additionalSignaturePage: nextPage, default: nextDefault },
      });
    }
    return [
      roleKey,
      { ...roleValue, max: slots, additionalSignaturePage: nextPage, default: nextDefault },
    ];
  });

  const pruned = Object.fromEntries(withCounts);

  const allowed = new Set();
  for (const [roleKey, roleValue] of prunedEntries) {
    for (const s of roleValue.signatures || []) {
      if (s?.signerKey) allowed.add(`${roleKey}\t${s.signerKey}`);
    }
  }
  const keptBindings = signerAttributeBindings.filter((b) => allowed.has(`${b?.roleKey}\t${b?.signerKey}`));

  const initialsAnnotations = state.initialsAnnotations || [];
  const hasInitials = initialsAnnotations.some((a) => a?.signerKey || a?.pdfLocation);
  // Every signer would go and nothing initials the form either — the shape of an unfinished form,
  // not of one that is initialled instead of signed.
  const prunesEverything = Object.keys(pruned).length === 0 && removed.length > 0;

  return {
    signatures: pruned,
    signerAttributeBindings: keptBindings,
    removed,
    slotFixes,
    hasInitials,
    flagged: prunesEverything && !hasInitials,
    changed:
      removed.length > 0 ||
      slotFixes.length > 0 ||
      keptBindings.length !== signerAttributeBindings.length,
  };
}
